import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GUIDE_ENTRY_ROUTE } from '@/features/auth/constants';

const benefits = [
  { icon: ScheduleOutlinedIcon, title: 'Flexible Schedule', description: 'Work when you want' },
  { icon: PublicOutlinedIcon, title: 'Global Reach', description: 'Meet travelers worldwide' },
  { icon: AccountBalanceWalletOutlinedIcon, title: 'Secure Payments', description: 'Get paid reliably' },
  { icon: TrendingUpOutlinedIcon, title: 'Grow Your Brand', description: 'Build your reputation' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] as const },
  },
};

export function BecomeGuideSection() {
  return (
    <Box
      component="section"
      sx={{
        bgcolor: 'background.default',
        py: { xs: 6, sm: 8, md: 10 },
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg" disableGutters sx={{ px: { xs: 2, sm: 3, lg: 3 } }}>
        <Box
          sx={{
            bgcolor: '#0F172A',
            borderRadius: { xs: 3, lg: 4 },
            overflow: 'hidden',
          }}
        >
          <Grid container>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 320, md: '100%' },
                  minHeight: { md: 520 },
                }}
              >
                <Box
                  component="img"
                  src="/images/destinations/guide-travelers.jpg"
                  alt="Local guide sharing knowledge with travelers"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center 25%',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: {
                      xs: 'linear-gradient(to bottom, transparent 50%, #0F172A 100%)',
                      md: 'linear-gradient(to right, transparent 60%, #0F172A 100%)',
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component={motion.div}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                sx={{
                  p: { xs: 3, sm: 4, md: 6, lg: 8 },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#60A5FA',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    mb: 2,
                  }}
                >
                  Become a Guide
                </Typography>

                <Typography
                  component="h2"
                  sx={{
                    fontFamily: '"Playfair Display", Georgia, serif',
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    fontWeight: 600,
                    color: 'white',
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                    mb: 2,
                  }}
                >
                  Share Your Passion,
                  <br />
                  Earn on Your Terms
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.6,
                    mb: 4,
                    maxWidth: 420,
                  }}
                >
                  We&apos;re onboarding guides in Agra, Delhi, Varanasi, Mathura, Vrindavan & Jaipur.
                  Turn your local knowledge into income and share what you love with travelers.
                </Typography>

                <Grid container spacing={{ xs: 2, md: 2 }} sx={{ mb: 4 }}>
                  {benefits.map(({ icon: Icon, title, description }) => (
                    <Grid key={title} size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: 'rgba(96, 165, 250, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ color: '#60A5FA', fontSize: 20 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '0.9375rem',
                              fontWeight: 600,
                              color: 'white',
                              lineHeight: 1.3,
                              mb: 0.25,
                            }}
                          >
                            {title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.8125rem',
                              color: 'rgba(255, 255, 255, 0.5)',
                            }}
                          >
                            {description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Button
                  component={RouterLink}
                  to={GUIDE_ENTRY_ROUTE}
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                    bgcolor: 'white',
                    color: '#0F172A',
                    px: 3.5,
                    py: 1.5,
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    borderRadius: 1,
                    boxShadow: 'none',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 200ms ease',
                  }}
                >
                  Start Guiding Today
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
